import React, { useState } from 'react';
import { X } from 'lucide-react';

interface NPSSurveyProps {
  onClose: () => void;
}

const NPSSurvey: React.FC<NPSSurveyProps> = ({ onClose }) => {
  const [score, setScore] = useState<number | null>(null);
  const [feedback, setFeedback] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    if (score === null) return;

    console.log('NPS Submission:', { score, feedback });
    // In production, send to backend
    setSubmitted(true);

    setTimeout(() => {
      onClose();
    }, 2000);
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-green-500">
        <div className="text-center">
          <div className="text-4xl mb-2">✓</div>
          <p className="text-gray-900 font-medium">Thank you for your feedback!</p>
          <p className="text-sm text-gray-600 mt-2">Your response helps us improve.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-blue-500">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">How likely are you to recommend this tool?</h3>
          <p className="text-sm text-gray-600 mt-1">Your feedback helps us improve provider experience.</p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* NPS Scale */}
      <div className="grid grid-cols-5 gap-2 md:grid-cols-10 mb-6">
        {Array.from({ length: 10 }, (_, i) => i).map(i => (
          <button
            key={i}
            onClick={() => setScore(i)}
            className={`aspect-square rounded flex items-center justify-center font-medium transition ${
              score === i
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {i}
          </button>
        ))}
      </div>

      {/* Scale Labels */}
      <div className="flex justify-between text-xs text-gray-500 mb-6">
        <span>Not at all likely</span>
        <span>Extremely likely</span>
      </div>

      {/* Feedback Text */}
      {score !== null && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            What's your main reason? (optional)
          </label>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="E.g., Reduced alert fatigue, saved time on documentation..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
            rows={3}
          />
        </div>
      )}

      {/* Submit Button */}
      <button
        onClick={handleSubmit}
        disabled={score === null}
        className={`w-full px-4 py-2 rounded-lg font-medium transition ${
          score === null
            ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
            : 'bg-blue-600 text-white hover:bg-blue-700'
        }`}
      >
        Submit Feedback
      </button>

      {/* Score Category Display */}
      {score !== null && (
        <div className="mt-4 p-3 rounded-lg bg-gray-50 text-sm">
          <p className="text-gray-700">
            {score >= 9 ? (
              <span>
                <strong>Promoter</strong> - Thank you! We'd love your recommendation.
              </span>
            ) : score >= 7 ? (
              <span>
                <strong>Passive</strong> - Good to know. Any suggestions for improvement?
              </span>
            ) : (
              <span>
                <strong>Detractor</strong> - We'd like to hear how to improve this tool.
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default NPSSurvey;
